import styled from 'styled-components';
import { useTranslation } from 'next-i18next';
import { EntityTypes } from '../../../../new-lib/constants/common';
import NewLogo from '../../../../new-components/ui/NewLogo';
import EntityPrices from '../components/EntityPrices';
import { useEffect, useRef, useState } from 'react';
import { Compound } from '../../../../new-lib/types/entity';
import { useRouter } from 'next/router';
import FiltersAlert from './FiltersAlert';

interface ChildProps {
  entity: Compound;
  entityType: EntityTypes;
  setStickyDetailsHeight: (height: number) => void;
}

const StickyDetails = ({
  entity,
  setStickyDetailsHeight
}: ChildProps): JSX.Element => {
  const { t } = useTranslation(['entity', 'common']);
  const stickyDetailsRef = useRef<HTMLDivElement>(null);
  const [isStuck, setIsStuck] = useState(false);
  const router = useRouter();

  useEffect(() => {
    if (stickyDetailsRef.current) {
      setStickyDetailsHeight(stickyDetailsRef.current.offsetHeight);
    }
  }, [stickyDetailsRef.current]);

  const handleLogoClick = () => {
    router.push({
      pathname: `/developer/${entity?.developerId}`
    });
  };

  useEffect(() => {
    const handleScroll = () => {
      if (!stickyDetailsRef.current) return;
      const rect = stickyDetailsRef.current.getBoundingClientRect();
      setIsStuck(rect.top <= 70);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return (
    <Container id="entity-data" ref={stickyDetailsRef} $isStuck={isStuck}>
      <div className="data-container container-fluid">
        <div className="logo-side">
          <NewLogo
            logoPath={entity?.developerLogoUrl}
            handleClick={handleLogoClick}
          />
        </div>

        <div className="compound-details">
          <span className="location">
            {`${entity.areaName}, ${t('egypt', { ns: 'common' })}`}{' '}
            {entity?.isLaunch && (
              <LaunchTag>{t('new_launch', { ns: 'common' })} </LaunchTag>
            )}
          </span>
          <h1 className="compound-name">{entity.name}</h1>
        </div>
        <div className="price-side">
          <FiltersAlert />
          <EntityPrices entity={entity} />
        </div>
      </div>
    </Container>
  );
};

const Container = styled.div<{ $isStuck?: boolean }>`
  width: 100%;
  background: ${({ $isStuck }) =>
    $isStuck
      ? 'linear-gradient(rgba(255, 255, 255, 0.9) 0%, rgba(250, 250, 250, 0.9) 100%)'
      : 'linear-gradient(rgba(255, 255, 255, 0.1) 0%, rgba(250, 250, 250, 0.1) 100%)'};
  position: absolute;
  top: -100px;
  z-index: 9;
  display: flex;
  align-items: center;
  padding: 12px 0;

  .data-container {
    display: flex;
    gap: 16px;
    align-items: center;
    .logo-side {
      width: 71px;
      height: 71px;
    }

    .compound-details {
      display: flex;
      flex-direction: column;
      gap: 8px;
      flex: 1;
      .location {
        display: flex;
        flex-direction: row;
        gap: 12px;
        font-size: 16px;
        color: #3e4042;
        line-height: 150%;
        font-family: materialRegular;
      }
      .compound-name {
        font-size: 24px;
        color: #2c3138;
        font-family: materialBold;
        line-height: 22px;
      }
    }
    .price-side {
      height: 100%;
      width: max-content;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      gap: 12px;
    }
  }
`;

const LaunchTag = styled.div`
  display: flex;
  padding: 4px 10px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 100px;
  background: linear-gradient(
    97deg,
    #ffd080 -24.55%,
    #ffdda4 50.93%,
    #ffd080 117.46%
  );
`;

export default StickyDetails;
